import React, { useEffect, useState } from 'react';
import Sidebar from '@/components/Sidebar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Calendar,
  CheckCircle,
  Clock,
  Trash2,
  User,
  AlertCircle,
  RefreshCw,
  CheckCheck,
  FileDown,
  Bell,
  Edit,
  Archive,
  Undo2,
  Package,
} from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export default function ScheduledDeliveries() {
  const [schedules, setSchedules] = useState([]);
  const [families, setFamilies] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('confirmed');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, scheduleId: null });
  const [clearCompletedDialog, setClearCompletedDialog] = useState(false);
  const [selectedSchedules, setSelectedSchedules] = useState([]);
  const [bulkCompleting, setBulkCompleting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingArchivedPdf, setExportingArchivedPdf] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [undoingId, setUndoingId] = useState(null);

  // Edit delivery modal state
  const [editModal, setEditModal] = useState({ open: false, schedule: null });
  const [newDeliveryDate, setNewDeliveryDate] = useState('');
  const [newDeliveryTime, setNewDeliveryTime] = useState('');
  const [resendConfirmation, setResendConfirmation] = useState(true);
  const [updatingDelivery, setUpdatingDelivery] = useState(false);

  const timeSlots = [
    '9:00 AM - 11:00 AM',
    '11:00 AM - 1:00 PM',
    '1:00 PM - 3:00 PM',
    '3:00 PM - 5:00 PM',
  ];

  useEffect(() => {
    fetchData();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') fetchData();
    };
    const handleFocus = () => fetchData();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const timestamp = Date.now();
      const [schedulesRes, familiesRes] = await Promise.all([
        api.get(`/delivery-schedules?_t=${timestamp}`),
        api.get(`/families?_t=${timestamp}`),
      ]);

      setSchedules(schedulesRes.data);

      const familyMap = {};
      familiesRes.data.forEach((f) => {
        familyMap[f.id] = f;
      });
      setFamilies(familyMap);
      setSelectedSchedules([]);
    } catch (error) {
      toast.error('Failed to fetch schedules');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    toast.info('Refreshing delivery data...');
    await fetchData();
    toast.success('Data refreshed successfully!');
  };

  const handleSendReminder = async (scheduleId) => {
    setSendingReminder(scheduleId);
    try {
      await api.post(`/delivery-schedules/${scheduleId}/send-reminder`);
      toast.success('Delivery reminder sent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminder(null);
    }
  };

  const openEditModal = (schedule) => {
    setEditModal({ open: true, schedule });
    setNewDeliveryDate(schedule.confirmed_slot?.date || '');
    setNewDeliveryTime(schedule.confirmed_slot?.time_slot || '');
    setResendConfirmation(true);
  };

  const handleUpdateDelivery = async () => {
    if (!newDeliveryDate || !newDeliveryTime) {
      toast.error('Please select both date and time');
      return;
    }
    setUpdatingDelivery(true);
    try {
      await api.put(`/delivery-schedules/${editModal.schedule.id}`, {
        date: newDeliveryDate,
        time_slot: newDeliveryTime,
        resend_confirmation: resendConfirmation,
      });
      toast.success(
        resendConfirmation
          ? 'Delivery updated and confirmation sent!'
          : 'Delivery updated successfully!'
      );
      setEditModal({ open: false, schedule: null });
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update delivery');
    } finally {
      setUpdatingDelivery(false);
    }
  };

  const handleCompleteDelivery = async (scheduleId) => {
    try {
      await api.post(`/delivery-schedules/${scheduleId}/complete`);
      toast.success('Delivery marked as completed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update delivery');
    }
  };

  const handleUndoComplete = async (scheduleId) => {
    setUndoingId(scheduleId);
    try {
      await api.post(`/delivery-schedules/${scheduleId}/undo-complete`);
      toast.success('Delivery moved back to Confirmed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to undo delivery');
    } finally {
      setUndoingId(null);
    }
  };

  const handleUndoFamilyDelivered = async (familyId) => {
    setUndoingId(familyId);
    try {
      await api.post(`/admin/undo-delivered/${familyId}`);
      toast.success('Family reverted to active delivery');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to undo delivery');
    } finally {
      setUndoingId(null);
    }
  };

  const handleBulkComplete = async () => {
    if (selectedSchedules.length === 0) {
      toast.error('Please select at least one delivery');
      return;
    }
    setBulkCompleting(true);
    try {
      const familyIds = selectedSchedules
        .map((scheduleId) => schedules.find((s) => s.id === scheduleId)?.family_id)
        .filter(Boolean);

      const response = await api.post('/delivery-schedules/bulk-complete', {
        family_ids: familyIds,
      });

      toast.success(response.data.message);
      setSelectedSchedules([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete deliveries');
    } finally {
      setBulkCompleting(false);
    }
  };

  const toggleSelectSchedule = (scheduleId) => {
    setSelectedSchedules((prev) =>
      prev.includes(scheduleId)
        ? prev.filter((id) => id !== scheduleId)
        : [...prev, scheduleId]
    );
  };

  const selectAllConfirmed = () => {
    const confirmedIds = schedules
      .filter((s) => s.status === 'confirmed')
      .map((s) => s.id);
    setSelectedSchedules(confirmedIds);
  };

  const clearSelection = () => setSelectedSchedules([]);

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await api.get('/delivery-schedules/export/pdf', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `confirmed_deliveries_${new Date().toISOString().split('T')[0]}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('No confirmed deliveries to export');
      } else {
        toast.error('Failed to export PDF');
      }
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportArchivedPdf = async () => {
    setExportingArchivedPdf(true);
    try {
      const response = await api.get('/delivery-schedules/archived/export-pdf', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `archived_deliveries_${new Date().toISOString().split('T')[0]}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Archived PDF downloaded successfully');
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('No archived deliveries to export');
      } else {
        toast.error('Failed to export PDF');
      }
    } finally {
      setExportingArchivedPdf(false);
    }
  };

  const handleDeleteSchedule = async () => {
    if (!deleteDialog.scheduleId) return;
    try {
      await api.delete(`/delivery-schedules/${deleteDialog.scheduleId}`);
      toast.success('Schedule deleted successfully');
      setDeleteDialog({ open: false, scheduleId: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete schedule');
    }
  };

  const handleClearCompleted = async () => {
    try {
      const response = await api.delete('/delivery-schedules/bulk/completed');
      toast.success(response.data.message);
      setClearCompletedDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear completed schedules');
    }
  };

  // Split schedules by status
  const confirmedSchedules = schedules
    .filter((s) => s.status === 'confirmed')
    .sort((a, b) =>
      (a.confirmed_slot?.date || '').localeCompare(b.confirmed_slot?.date || '')
    );
  const completedSchedules = schedules.filter((s) => s.status === 'completed');
  const scheduledFamilyIds = new Set(completedSchedules.map((s) => s.family_id));

  // Families marked as delivered manually (no completed schedule) — synthesize archive entries
  const manuallyDeliveredFamilies = Object.values(families).filter(
    (f) => f.delivery_status === 'delivered' && !scheduledFamilyIds.has(f.id)
  );
  const manualArchivedEntries = manuallyDeliveredFamilies.map((f) => ({
    id: `manual-${f.id}`,
    family_id: f.id,
    status: 'completed',
    isManual: true,
    confirmed_slot: f.delivered_at ? { date: f.delivered_at, time_slot: null } : null,
    completed_at: f.delivery_confirmed_at || null,
    delivery_items: [],
    delivery_notes: f.delivery_notes || null,
  }));

  const archivedSchedules = [...completedSchedules, ...manualArchivedEntries].sort(
    (a, b) =>
      (b.confirmed_slot?.date || b.completed_at || '').localeCompare(
        a.confirmed_slot?.date || a.completed_at || ''
      )
  );

  const renderFamilyBlock = (schedule) => {
    const family = families[schedule.family_id];
    const familyName = family
      ? `${family.parent_first_name} ${family.parent_last_name}`
      : schedule.family_name || 'Unknown Family';
    const familyEmail = family?.email || schedule.family_email || null;
    const familyPhone = family?.phone || schedule.family_phone || null;
    const familyAddress = family?.address || schedule.family_address || null;

    return (
      <div className="p-3 bg-stone-50 rounded-lg mb-3">
        <div className="flex items-center gap-2 mb-2">
          <User className="w-4 h-4 text-primary" />
          <span
            className="font-semibold text-primary"
            data-testid={`family-name-${schedule.id}`}
          >
            {familyName}
          </span>
          {!family && (
            <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
              <AlertCircle className="w-3 h-3 mr-1" />
              Profile not found
            </Badge>
          )}
        </div>
        {family ? (
          <div className="text-sm text-stone-600 space-y-1">
            <p>{family.address}</p>
            <p>
              {family.phone} • {family.email}
            </p>
            {family.ward_number && <p>Ward {family.ward_number}</p>}
            {family.city_county && <p>{family.city_county}</p>}
          </div>
        ) : (
          <div className="text-sm text-stone-500">
            {familyAddress && <p>{familyAddress}</p>}
            {(familyPhone || familyEmail) && (
              <p>{[familyPhone, familyEmail].filter(Boolean).join(' • ')}</p>
            )}
            {!familyAddress && !familyPhone && !familyEmail && (
              <p className="italic">No contact information available</p>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderDeliveryItems = (schedule, variant = 'green') => {
    if (!schedule.delivery_items || schedule.delivery_items.length === 0) return null;
    const palette =
      variant === 'amber'
        ? 'bg-amber-50 border-amber-200'
        : 'bg-green-50 border-green-200';
    return (
      <div className={`p-3 rounded-lg border ${palette}`}>
        <p className="text-sm font-medium text-stone-700 mb-2 flex items-center gap-1">
          <Package className="w-4 h-4" /> Items {variant === 'amber' ? 'Delivered' : 'Assigned'}:
        </p>
        <div className="grid grid-cols-2 gap-2 text-sm">
          {schedule.delivery_items.map((item, idx) => (
            <div key={idx} className="flex justify-between">
              <span className="text-stone-600">{item.item_type}:</span>
              <span className="font-medium">{item.quantity}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const confirmedCount = confirmedSchedules.length;
  const archivedCount = archivedSchedules.length;

  return (
    <div className="flex">
      <Sidebar />
      <div className="ml-64 flex-1 p-8" data-testid="scheduled-deliveries-page">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-4xl font-bold text-foreground mb-2">Deliveries</h1>
              <p className="text-stone-600">
                Manage confirmed deliveries and review archived deliveries
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleRefresh}
                disabled={loading}
                className="rounded-full"
                data-testid="refresh-button"
              >
                <RefreshCw
                  className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`}
                />
                Refresh
              </Button>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="mb-6" data-testid="delivery-tabs">
              <TabsTrigger value="confirmed" data-testid="tab-confirmed">
                <Calendar className="w-4 h-4 mr-2" />
                Confirmed ({confirmedCount})
              </TabsTrigger>
              <TabsTrigger value="archived" data-testid="tab-archived">
                <Archive className="w-4 h-4 mr-2" />
                Archived Delivered ({archivedCount})
              </TabsTrigger>
            </TabsList>

            {/* CONFIRMED TAB */}
            <TabsContent value="confirmed">
              <div className="flex justify-end mb-4 gap-2">
                <Button
                  variant="outline"
                  onClick={handleExportPdf}
                  disabled={exportingPdf || confirmedSchedules.length === 0}
                  className="rounded-full"
                  data-testid="export-pdf-button"
                >
                  <FileDown
                    className={`w-4 h-4 mr-2 ${exportingPdf ? 'animate-pulse' : ''}`}
                  />
                  {exportingPdf ? 'Exporting...' : 'Export Confirmed PDF'}
                </Button>
              </div>

              {/* Bulk Action Bar */}
              {confirmedCount > 0 && (
                <Card className="border-stone-200 mb-6">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={selectAllConfirmed}
                          data-testid="select-all-confirmed"
                        >
                          Select All Confirmed ({confirmedCount})
                        </Button>
                        {selectedSchedules.length > 0 && (
                          <Button variant="ghost" size="sm" onClick={clearSelection}>
                            Clear Selection
                          </Button>
                        )}
                        {selectedSchedules.length > 0 && (
                          <span className="text-sm text-stone-600">
                            {selectedSchedules.length} selected
                          </span>
                        )}
                      </div>
                      {selectedSchedules.length > 0 && (
                        <Button
                          onClick={handleBulkComplete}
                          disabled={bulkCompleting}
                          className="rounded-full"
                          data-testid="bulk-complete-button"
                        >
                          <CheckCheck className="w-4 h-4 mr-2" />
                          {bulkCompleting
                            ? 'Completing...'
                            : `Mark ${selectedSchedules.length} Delivered`}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {loading ? (
                <div className="text-center py-12">Loading...</div>
              ) : confirmedSchedules.length === 0 ? (
                <Card className="border-stone-200 shadow-card">
                  <CardContent className="py-12 text-center">
                    <Calendar className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                    <p className="text-stone-600 mb-2">No confirmed deliveries</p>
                    <p className="text-sm text-stone-500">
                      Confirmed deliveries will appear here
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4" data-testid="deliveries-list">
                  {confirmedSchedules.map((schedule) => {
                    const isSelected = selectedSchedules.includes(schedule.id);
                    const deliveryDate = schedule.confirmed_slot?.date
                      ? new Date(
                          schedule.confirmed_slot.date + 'T12:00:00'
                        ).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : 'Date not set';
                    return (
                      <Card
                        key={schedule.id}
                        className={`border-stone-200 shadow-card transition-all ${
                          isSelected ? 'border-primary ring-2 ring-primary/20' : ''
                        }`}
                        data-testid={`schedule-${schedule.id}`}
                      >
                        <CardContent className="p-5">
                          <div className="flex justify-between items-start">
                            <div className="flex gap-3 flex-1">
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => toggleSelectSchedule(schedule.id)}
                                className="mt-1"
                                data-testid={`select-${schedule.id}`}
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-3 flex-wrap">
                                  <div className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-primary" />
                                    <span className="font-semibold text-lg">{deliveryDate}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Clock className="w-4 h-4 text-stone-500" />
                                    <span className="font-medium">
                                      {schedule.confirmed_slot?.time_slot || 'Time not set'}
                                    </span>
                                  </div>
                                  <Badge className="bg-blue-100 text-blue-800">confirmed</Badge>
                                </div>

                                {renderFamilyBlock(schedule)}
                                {renderDeliveryItems(schedule, 'green')}
                              </div>
                            </div>

                            <div className="flex items-center gap-2 flex-wrap">
                              <Button
                                variant="outline"
                                onClick={() => openEditModal(schedule)}
                                className="rounded-full"
                                data-testid={`edit-${schedule.id}`}
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Edit Date/Time
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => handleSendReminder(schedule.id)}
                                disabled={sendingReminder === schedule.id}
                                className="rounded-full"
                                data-testid={`reminder-${schedule.id}`}
                              >
                                <Bell
                                  className={`w-4 h-4 mr-2 ${
                                    sendingReminder === schedule.id ? 'animate-pulse' : ''
                                  }`}
                                />
                                {sendingReminder === schedule.id ? 'Sending...' : 'Send Reminder'}
                              </Button>
                              <Button
                                onClick={() => handleCompleteDelivery(schedule.id)}
                                className="rounded-full"
                                data-testid={`complete-${schedule.id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Mark Delivered
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  setDeleteDialog({ open: true, scheduleId: schedule.id })
                                }
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-${schedule.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            {/* ARCHIVED TAB */}
            <TabsContent value="archived">
              <div className="flex justify-end mb-4 gap-2">
                <Button
                  variant="outline"
                  onClick={handleExportArchivedPdf}
                  disabled={exportingArchivedPdf || archivedSchedules.length === 0}
                  className="rounded-full"
                  data-testid="export-archived-pdf-button"
                >
                  <FileDown
                    className={`w-4 h-4 mr-2 ${
                      exportingArchivedPdf ? 'animate-pulse' : ''
                    }`}
                  />
                  {exportingArchivedPdf ? 'Exporting...' : 'Export Archived PDF'}
                </Button>
                {archivedCount > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => setClearCompletedDialog(true)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full"
                    data-testid="clear-completed-button"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear All Archived ({archivedCount})
                  </Button>
                )}
              </div>

              {loading ? (
                <div className="text-center py-12">Loading...</div>
              ) : archivedSchedules.length === 0 ? (
                <Card className="border-stone-200 shadow-card">
                  <CardContent className="py-12 text-center">
                    <Archive className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                    <p className="text-stone-600 mb-2">No archived deliveries yet</p>
                    <p className="text-sm text-stone-500">
                      Once you mark a delivery as delivered, it will appear here for reporting and export.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4" data-testid="archived-list">
                  {archivedSchedules.map((schedule) => {
                    const deliveryDate = schedule.confirmed_slot?.date
                      ? new Date(
                          schedule.confirmed_slot.date + 'T12:00:00'
                        ).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : schedule.completed_at
                      ? new Date(schedule.completed_at).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : 'Date not set';
                    return (
                      <Card
                        key={schedule.id}
                        className="border-stone-200 shadow-card border-l-4 border-l-green-500"
                        data-testid={`archived-${schedule.id}`}
                      >
                        <CardContent className="p-5">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-3 flex-wrap">
                                <div className="flex items-center gap-2">
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                  <span className="font-semibold text-lg">{deliveryDate}</span>
                                </div>
                                {schedule.confirmed_slot?.time_slot && (
                                  <div className="flex items-center gap-2">
                                    <Clock className="w-4 h-4 text-stone-500" />
                                    <span className="font-medium">
                                      {schedule.confirmed_slot.time_slot}
                                    </span>
                                  </div>
                                )}
                                <Badge className="bg-green-100 text-green-800">delivered</Badge>
                                {schedule.isManual && (
                                  <Badge variant="outline" className="text-xs text-amber-700 border-amber-300">
                                    Manual entry
                                  </Badge>
                                )}
                              </div>

                              {renderFamilyBlock(schedule)}
                              {renderDeliveryItems(schedule, 'amber')}
                            </div>

                            <div className="flex items-center gap-2 flex-wrap">
                              <Button
                                variant="outline"
                                onClick={() =>
                                  schedule.isManual
                                    ? handleUndoFamilyDelivered(schedule.family_id)
                                    : handleUndoComplete(schedule.id)
                                }
                                disabled={
                                  undoingId === schedule.id ||
                                  undoingId === schedule.family_id
                                }
                                className="rounded-full"
                                data-testid={`undo-${schedule.id}`}
                              >
                                <Undo2
                                  className={`w-4 h-4 mr-2 ${
                                    undoingId === schedule.id ||
                                    undoingId === schedule.family_id
                                      ? 'animate-pulse'
                                      : ''
                                  }`}
                                />
                                {undoingId === schedule.id ||
                                undoingId === schedule.family_id
                                  ? 'Reverting...'
                                  : 'Undo Delivered'}
                              </Button>
                              {!schedule.isManual && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    setDeleteDialog({ open: true, scheduleId: schedule.id })
                                  }
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-archived-${schedule.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Delete Single Schedule Dialog */}
      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) => !open && setDeleteDialog({ open: false, scheduleId: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Delivery Schedule?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this delivery schedule. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSchedule}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clear Completed Dialog */}
      <AlertDialog open={clearCompletedDialog} onOpenChange={setClearCompletedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear All Archived Deliveries?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all completed/archived delivery records.
              Family records will remain. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearCompleted}
              className="bg-red-600 hover:bg-red-700"
            >
              Clear All Archived
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Delivery Modal */}
      <Dialog
        open={editModal.open}
        onOpenChange={(open) => !open && setEditModal({ open: false, schedule: null })}
      >
        <DialogContent className="max-w-md" data-testid="edit-delivery-modal">
          <DialogHeader>
            <DialogTitle>Edit Delivery Schedule</DialogTitle>
            <DialogDescription>
              {editModal.schedule && families[editModal.schedule.family_id]
                ? `${families[editModal.schedule.family_id].parent_first_name} ${
                    families[editModal.schedule.family_id].parent_last_name
                  }`
                : editModal.schedule?.family_name || 'Family'}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="p-4 bg-stone-50 rounded-lg">
              <p className="text-sm font-medium text-stone-600 mb-2">Current Schedule</p>
              <div className="flex items-center gap-2 text-foreground">
                <Calendar className="w-4 h-4 text-primary" />
                <span>{editModal.schedule?.confirmed_slot?.date || 'Not set'}</span>
              </div>
              <div className="flex items-center gap-2 text-foreground mt-1">
                <Clock className="w-4 h-4 text-primary" />
                <span>{editModal.schedule?.confirmed_slot?.time_slot || 'Not set'}</span>
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-sm font-medium text-stone-600">New Schedule</p>

              <div>
                <Label htmlFor="edit-delivery-date">Delivery Date</Label>
                <Input
                  id="edit-delivery-date"
                  type="date"
                  value={newDeliveryDate}
                  onChange={(e) => setNewDeliveryDate(e.target.value)}
                  className="mt-1"
                  data-testid="edit-delivery-date-input"
                />
              </div>

              <div>
                <Label htmlFor="edit-delivery-time">Time Slot</Label>
                <select
                  id="edit-delivery-time"
                  value={newDeliveryTime}
                  onChange={(e) => setNewDeliveryTime(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20 mt-1"
                  data-testid="edit-delivery-time-select"
                >
                  <option value="">Select time slot</option>
                  {timeSlots.map((slot) => (
                    <option key={slot} value={slot}>
                      {slot}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="edit-resend-confirmation"
                  checked={resendConfirmation}
                  onCheckedChange={setResendConfirmation}
                  data-testid="edit-resend-confirmation-checkbox"
                />
                <Label
                  htmlFor="edit-resend-confirmation"
                  className="text-sm font-normal cursor-pointer"
                >
                  Send updated confirmation email to family
                </Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditModal({ open: false, schedule: null })}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateDelivery}
              disabled={updatingDelivery || !newDeliveryDate || !newDeliveryTime}
              className="rounded-full"
              data-testid="save-delivery-button"
            >
              {updatingDelivery ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
